import React, { useState, createContext, useContext, useCallback, useMemo, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Download, X, Check, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useLang } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PresentationContext = createContext(null);

export const PresentationProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ open, openModal, closeModal }), [open, openModal, closeModal]);
  return (
    <PresentationContext.Provider value={value}>
      {children}
      <PresentationModal />
    </PresentationContext.Provider>
  );
};

export const usePresentation = () => {
  const ctx = useContext(PresentationContext);
  if (!ctx) throw new Error("usePresentation must be used within PresentationProvider");
  return ctx;
};

function PresentationModal() {
  const { open, closeModal } = usePresentation();
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    role: "",
    template: "oil_gas",
    sector_focus: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setResult(null);
    setForm({
      name: "",
      company: "",
      email: "",
      phone: "",
      country: "",
      role: "",
      template: "oil_gas",
      sector_focus: "",
      consent: false,
    });
  };

  const handleClose = () => {
    closeModal();
    setTimeout(reset, 300);
  };

  // Escape key closes the modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    // prevent body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const triggerDownload = (downloadUrl, companyName) => {
    const a = document.createElement("a");
    a.href = `${BACKEND_URL}${downloadUrl}`;
    a.download = `DEPPCM_Capability_Statement_${companyName || "client"}.pdf`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.company || !form.email) {
      toast.error(lang === "es" ? "Completa los campos requeridos" : "Please fill required fields");
      return;
    }
    if (!form.consent) {
      toast.error(
        lang === "es"
          ? "Debes aceptar el Aviso de Privacidad para continuar."
          : "Please accept the Privacy Notice to continue."
      );
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, language: lang };
      const { data } = await axios.post(`${API}/presentation/request`, payload);
      toast.success(t.presentation.success);
      triggerDownload(data.download_url, data.company);
      setResult(data);
    } catch (err) {
      console.error(err);
      toast.error(t.presentation.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-md"
          data-testid="presentation-modal"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative my-8 mx-4 w-full max-w-3xl bg-[#0F0F11] border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* corner brackets */}
            <div className="absolute top-4 left-4 w-5 h-5 border-t border-l border-[#D4AF37]" />
            <div className="absolute top-4 right-4 w-5 h-5 border-t border-r border-[#D4AF37]" />
            <div className="absolute bottom-4 left-4 w-5 h-5 border-b border-l border-[#D4AF37]" />
            <div className="absolute bottom-4 right-4 w-5 h-5 border-b border-r border-[#D4AF37]" />

            <button
              onClick={handleClose}
              data-testid="presentation-close"
              className="absolute top-5 right-5 z-10 text-white/60 hover:text-white transition-colors p-1.5"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-8 sm:p-10 lg:p-12">
              {result ? (
                <SuccessView
                  result={result}
                  onRetry={() => triggerDownload(result.download_url, result.company)}
                  onReset={reset}
                  onClose={handleClose}
                  lang={lang}
                  t={t}
                />
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="h-px w-10 bg-[#D4AF37]" />
                    <span className="overline">{t.presentation.modal_eyebrow}</span>
                  </div>
                  <h2 className="font-display font-bold text-white text-[clamp(1.5rem,3vw,2.25rem)] tracking-tight leading-[1.1] max-w-[22ch]">
                    {t.presentation.modal_title}
                  </h2>
                  <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed max-w-xl">
                    {t.presentation.modal_subtitle}
                  </p>

                  <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="presentation-form">
                    <div className="flex items-center justify-between pb-4 border-b border-white/5">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                        <Lock className="w-3 h-3" />
                        {lang === "es" ? "Canal Cifrado" : "Secure Channel"}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939C]">
                        <FileText className="w-3 h-3 inline-block mr-1" />
                        PDF · 7 pages
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field
                        testid="presentation-name"
                        label={t.presentation.fields.name}
                        value={form.name}
                        onChange={(v) => update("name", v)}
                        required
                      />
                      <Field
                        testid="presentation-company"
                        label={t.presentation.fields.company}
                        value={form.company}
                        onChange={(v) => update("company", v)}
                        required
                      />
                      <Field
                        testid="presentation-email"
                        label={t.presentation.fields.email}
                        type="email"
                        value={form.email}
                        onChange={(v) => update("email", v)}
                        required
                      />
                      <Field
                        testid="presentation-phone"
                        label={t.presentation.fields.phone}
                        value={form.phone}
                        onChange={(v) => update("phone", v)}
                      />
                      <Field
                        testid="presentation-country"
                        label={t.presentation.fields.country}
                        value={form.country}
                        onChange={(v) => update("country", v)}
                      />
                      <Field
                        testid="presentation-role"
                        label={t.presentation.fields.role}
                        value={form.role}
                        onChange={(v) => update("role", v)}
                      />
                      <div className="flex flex-col">
                        <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
                          {t.presentation.fields.template} *
                        </label>
                        <Select value={form.template} onValueChange={(v) => update("template", v)}>
                          <SelectTrigger
                            data-testid="presentation-template"
                            className="bg-transparent border-0 border-b border-white/15 rounded-none text-white px-0 focus:ring-0 focus:border-[#D4AF37] h-10 hover:border-white/30"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121214] border border-white/10 text-white rounded-none">
                            <SelectItem value="oil_gas" className="text-white focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]">
                              {t.presentation.templates.oil_gas}
                            </SelectItem>
                            <SelectItem value="corporate" className="text-white focus:bg-[#D4AF37]/10 focus:text-[#D4AF37]">
                              {t.presentation.templates.corporate}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Field
                        testid="presentation-sector"
                        label={t.presentation.fields.sector_focus}
                        value={form.sector_focus}
                        onChange={(v) => update("sector_focus", v)}
                      />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer select-none group pt-2">
                      <Checkbox
                        data-testid="presentation-consent"
                        checked={form.consent}
                        onCheckedChange={(v) => update("consent", !!v)}
                        className="mt-0.5 border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37] data-[state=checked]:text-black rounded-none"
                      />
                      <span className="text-[11px] leading-relaxed text-[#A1A1AA] group-hover:text-white transition-colors">
                        {t.presentation.fields.consent}{" "}
                        <Link
                          to="/privacidad"
                          target="_blank"
                          className="text-[#D4AF37] underline underline-offset-4 hover:text-[#F0C84B]"
                        >
                          {lang === "es" ? "Leer Aviso" : "Read Notice"}
                        </Link>
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="presentation-submit"
                      className="group w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 text-[12px] font-bold uppercase tracking-[0.25em] hover:bg-[#F0C84B] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? t.presentation.generating : t.presentation.submit}
                      {!loading && (
                        <Download className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="flex flex-col">
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#8B939C] mb-2">
        {label}
        {required && " *"}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="bg-transparent border-0 border-b border-white/15 rounded-none text-white text-sm px-0 py-2 focus:outline-none focus:border-[#D4AF37] placeholder:text-[#3f3f46] transition-colors hover:border-white/30"
      />
    </div>
  );
}

function SuccessView({ result, onRetry, onReset, onClose, lang, t }) {
  return (
    <div
      data-testid="presentation-success"
      className="min-h-[420px] flex flex-col items-center justify-center text-center py-8"
    >
      <div className="w-14 h-14 border border-[#D4AF37] bg-[#D4AF37]/10 flex items-center justify-center">
        <Check className="w-6 h-6 text-[#D4AF37]" />
      </div>
      <div className="overline mt-6">
        {lang === "es" ? "Descarga lista" : "Download ready"}
      </div>
      <div className="font-display font-bold text-white text-2xl mt-4 max-w-md">
        {t.presentation.success}
      </div>
      <div className="mt-3 text-sm text-[#8B939C]">
        {result.company}
      </div>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <button
          data-testid="presentation-retry"
          onClick={onRetry}
          className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          {t.presentation.success_retry}
        </button>
        <button
          data-testid="presentation-another"
          onClick={onReset}
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#F0C84B] transition-colors"
        >
          {lang === "es" ? "Nueva descarga" : "Another deck"}
        </button>
      </div>
    </div>
  );
}
